import type { AgentConfig, MediaSourceMode, MediaSourcesConfig } from "@/types/agent/agent";
import { SHOW_RELATED_MEDIA_TOOL_ID } from "@/utils/config/elevenlabs";

// Keep the modes, the cap and normalizeDomain in sync with
// supabase/functions/_shared/media-sources.ts, which cleans the list again on save.
export const MAX_MEDIA_SOURCE_DOMAINS = 10;

export const MEDIA_SOURCE_OPTIONS: Array<{ mode: MediaSourceMode; label: string; description: string }> = [
  {
    mode: "own_content",
    label: "My content only",
    description: "Only your Resources, the sites they link to, and your knowledge base. Never searches the rest of the web.",
  },
  {
    mode: "approved_sites",
    label: "Approved sites only",
    description: "Uses your own content first, then searches only the sites you list.",
  },
  {
    mode: "web",
    label: "Anywhere on the web",
    description: "Uses your own content first, then searches the whole web. Results aren't limited to sites you chose.",
  },
];

const HOSTNAME = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z][a-z0-9-]{1,62}$/;

/** Reduces whatever was typed (a URL, a host, with or without www) to a bare domain. */
export const normalizeDomain = (input: string | null | undefined): string | null => {
  const value = input?.trim().toLowerCase();
  if (!value) return null;

  try {
    const host = new URL(/^[a-z][a-z0-9+.-]*:\/\//.test(value) ? value : `https://${value}`).hostname
      .replace(/\.$/, "")
      .replace(/^www\./, "");
    return HOSTNAME.test(host) ? host : null;
  } catch {
    return null;
  }
};

/** A genie that never set this stays on its own content. */
export const readMediaSourcesConfig = (config?: Partial<MediaSourcesConfig> | null): MediaSourcesConfig => ({
  mode: MEDIA_SOURCE_OPTIONS.find((option) => option.mode === config?.mode)?.mode ?? "own_content",
  allowed_domains: Array.isArray(config?.allowed_domains) ? config.allowed_domains : [],
});

/** Whether the genie puts links and pictures in the chat itself (the Show Links & Images tool is on). */
export const hasInChatMedia = (settings?: AgentConfig | null): boolean =>
  !!settings?.conversation_config?.agent?.prompt?.tool_ids?.includes(SHOW_RELATED_MEDIA_TOOL_ID);

export const describeMediaSources =(config: MediaSourcesConfig): string => {
  const label = MEDIA_SOURCE_OPTIONS.find((option) => option.mode === config.mode)?.label ?? "";
  return config.mode === "approved_sites" && config.allowed_domains.length > 0
    ? `${label} · ${config.allowed_domains.join(", ")}`
    : label;
};
